import Core                       from '@/core/Core';
import Utils                      from '@/core/utils/Utils';
import TransportZone              from '@/domains/TransportZones/TransportZone';
import TransportZonesDAO          from '@/domains/TransportZones/TransportZonesDAO';

import type RpcDataPagination     from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort           from '@/core/services/Rpc/RpcDataSort';
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";

/**
 * Сервис для работы с транспортными зонами
 */
class TransportZonesService {

	/**
	 * Загружает одну зону по ID
	 */
	static async get(id: number): Promise<TransportZone | null> {
		const response = await TransportZonesDAO.get(id);

		if (!response.data) return null;

		return TransportZone.fromServer(response.data);
	}

	/**
	 * Загружает список транспортных зон с пагинацией.
	 * Используется в реестре через DataGridLoader.
	 */
	static async getListPaginated(
		params?: Parameters<typeof TransportZonesDAO.getList>[0] & {
			pagination?: RpcDataPagination;
			sort?: RpcDataSort;
		}
	): Promise<{ data: TransportZone[]; pagination: RpcDataPagination }> {
		const { pagination, sort, ...filters } = params ?? {};
		const response = await TransportZonesDAO.getList(filters, pagination, sort);

		return {
			data: Utils.hydrateArray(response.data, TransportZone.fromServer),
			pagination: response.meta.pagination,
		};
	}

	/**
	 * Загружает список транспортных зон.
	 * Для использования вне реестра (диалоги, автокомплиты).
	 */
	static async getList(params?: Parameters<typeof TransportZonesDAO.getList>[0]): Promise<TransportZone[]> {
		return (await this.getListPaginated(params)).data;
	}

	/**
	 * Создаёт транспортную зону
	 */
	static async add(transportZone: TransportZone): Promise<boolean> {
		const errors = transportZone.validate();
		if (errors.length > 0) {
			Core.showNotification.error(errors.join('\n'));
			return false;
		}

		const response = await TransportZonesDAO.add(transportZone);

		if (response.ok) {
			Core.showNotification.success('Транспортная зона добавлена');
		}

		return response.ok;
	}

	/**
	 * Обновляет транспортную зону
	 */
	static async update(transportZone: TransportZone): Promise<boolean> {
		const errors = transportZone.validate();
		if (errors.length > 0) {
			Core.showNotification.error(errors.join('\n'));
			return false;
		}

		const response = await TransportZonesDAO.update(transportZone);

		if (response.ok) {
			Core.showNotification.success('Транспортная зона обновлена');
		}

		return response.ok;
	}

	/**
	 * Удаляет транспортную зону
	 */
	static async delete(id: number): Promise<boolean> {
		const response = await TransportZonesDAO.delete(id);

		if (response.ok) {
			Core.showNotification.success('Транспортная зона удалена');
		}

		return response.ok;
	}


	// ============================================================
	// Диалоги
	// ============================================================

	/**
	 * Открыть карточку зоны.
	 * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
	 */
	static showTransportZoneCard(transportZone: TransportZone | null): Promise<boolean> {
		return Core.dialogs.TransportZoneCard.showAsPromise({ id: transportZone?.id ?? null });
	}

	/**
	 * Удаляет транспортную зону с подтверждением.
	 * Принимает сущность целиком — нужно её имя для текста подтверждения.
	 */
	static async deleteWithConfirmation(transportZone: TransportZone): Promise<boolean> {
		const confirmed = await Core.dialogs.confirm(
			'Удалить эту зону?',
			`Зона ${transportZone.name}`,
		);

		if (confirmed === true) {
			return await this.delete(transportZone.id);
		} else {
			return false;
		}
	}

	/**
	 * Пункты контекстного меню транспортной зоны для грида реестра и карточки.
	 * Грид обновляет список, а карточка закрывается через resolve(), только если
	 * callback пункта вернул true.
	 */
	static get contextMenu(): ContextMenuItem[] {
		return [
			new ContextMenuItem(
				'Открыть',
				'open_in_new',
				(transportZone: TransportZone) => { return this.showTransportZoneCard(transportZone); },
				null,
				false,  // hidden
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Удалить',
				'delete',
				(transportZone: TransportZone) => { return this.deleteWithConfirmation(transportZone); },
			),
		];
	}
}

export default TransportZonesService;